import React, { useMemo, useRef } from "react";
import { Badge } from "../../components/ui/badge";
import ResourceManager, { useResourceList } from "../../components/admin/ResourceManager";
import { fmtDate } from "../../lib/resources";

export default function ManageNotifications() {
  const [schools] = useResourceList("schools");
  const [classes] = useResourceList("classes");
  const schoolOpts = useMemo(() => schools.map((s) => ({ value: s.name, label: s.name })), [schools]);
  const classOpts = useMemo(() => classes.map((c) => ({ value: c.name, label: c.name })), [classes]);
  const prevAudienceRef = useRef("");

  const dynamicFields = useMemo(() => (fields, form) => {
    const audience = form?.audience || "";
    return fields.filter((f) => {
      if (audience === "all" && (f.key === "school_name" || f.key === "class_name")) return false;
      if (audience === "school" && f.key === "class_name") return false;
      return true;
    }).map((f) => {
      if (f.key === "class_name" && audience === "class") {
        const schoolName = form?.school_name || "";
        const filtered = schoolName
          ? classOpts
          : [];
        return { ...f, options: filtered, disabled: !schoolName };
      }
      if (f.key === "school_name") {
        return { ...f, required: audience === "school" || audience === "class" };
      }
      if (f.key === "class_name") {
        return { ...f, required: audience === "class" };
      }
      return f;
    });
  }, [classOpts]);

  const onFormChange = (form, setForm) => {
    const audience = form?.audience || "";
    if (prevAudienceRef.current !== audience && prevAudienceRef.current !== "") {
      const patch = {};
      if (audience !== "school" && audience !== "class") patch.school_name = "";
      if (audience !== "class") patch.class_name = [];
      if (Object.keys(patch).length) setForm?.((s) => ({ ...s, ...patch }));
    }
    prevAudienceRef.current = audience;
  };

  return (
    <ResourceManager
      kind="notifications"
      selectable={true}
      eyebrow="Communication"
      title="Notifications"
      subtitle="Send announcements to all students, a specific school, or one or more classes."
      excelEnabled={false}
      fields={[
        { key: "title", label: "Title", type: "text", required: true },
        { key: "audience", label: "Audience", type: "select", required: true, options: [
          { value: "all", label: "All students" },
          { value: "school", label: "Specific school" },
          { value: "class", label: "Specific class" },
        ] },
        { key: "school_name", label: "School", type: "select", options: schoolOpts, placeholder: "Pick school" },
        { key: "class_name", label: "Classes", type: "multi-select", options: classOpts, placeholder: "Pick one or more classes" },
        { key: "image", label: "Banner Image", type: "image" },
        { key: "body", label: "Message", type: "textarea", required: true, span: 2 },
      ]}
      columns={[
        { key: "image", label: "Banner", render: (r) => {
          const url = typeof r.image === "string" ? r.image : r.image?.url;
          return url ? <img src={url} alt="" loading="lazy" className="w-14 h-10 rounded-md object-cover border border-white/10" /> : <span className="text-[#64748B]">—</span>;
        }},
        { key: "title", label: "Title" },
        { key: "audience", label: "Audience", render: (r) => (
          <Badge variant="outline" className="border-violet-400/40 text-violet-200 bg-violet-500/10">{r.audience || "all"}</Badge>
        )},
        { key: "target", label: "Target", render: (r) => {
          if (r.audience === "school") return r.school_name;
          if (r.audience === "class") {
            const classes = Array.isArray(r.class_name) ? r.class_name : (r.class_name ? [r.class_name] : []);
            return classes.length ? classes.join(", ") : "—";
          }
          return "Everyone";
        }},
        { key: "body", label: "Message", render: (r) => <span className="text-white/80 line-clamp-1 max-w-[280px] inline-block truncate">{r.body || "—"}</span> },
        { key: "status", label: "Status", render: (r) => (
          <Badge variant="outline" className={r.status === "disabled" ? "border-red-400/40 text-red-300 bg-red-500/10" : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10"}>{r.status || "active"}</Badge>
        )},
        { key: "created_at", label: "Sent", render: (r) => fmtDate(r.created_at) },
      ]}
      filters={[
        { key: "audience", label: "Audience", options: [
          { value: "all", label: "All" }, { value: "school", label: "School" }, { value: "class", label: "Class" },
        ] },
      ]}
      downloadable
      viewable
      dynamicFields={dynamicFields}
      onFormChange={onFormChange}
    />
  );
}
